/**
 * Typed API client, derived entirely from `@vibelang/contracts`.
 *
 * Properties the client guarantees so that callers don't have to think about them:
 *   - responses are validated against the contract schema, so a server change that
 *     breaks the contract fails loudly here rather than corrupting the UI
 *   - errors arrive as RFC 9457 problem documents with a `retryable` flag
 *   - access tokens refresh transparently, once, with concurrent callers sharing the
 *     same refresh rather than stampeding
 *   - mutating POSTs carry an `Idempotency-Key`, so a retry is safe by default
 */

import {
  API_BASE_PATH,
  buildPath,
  type Problem,
  type RouteBody,
  type RouteName,
  type RouteParams,
  type RouteQuery,
  type RouteResponse,
  routes,
} from '@vibelang/contracts';

export class ApiError extends Error {
  constructor(
    readonly problem: Problem,
    readonly status: number,
  ) {
    super(problem.title);
    this.name = 'ApiError';
  }

  get retryable(): boolean {
    return this.problem.retryable;
  }

  /**
   * True when the learner outran a request budget. Distinct from `isQuota`: a quota is
   * a product boundary with a plan behind it, this is "you are going faster than we
   * serve" and clears on its own.
   */
  get isRateLimited(): boolean {
    return this.status === 429;
  }

  /** The server's own back-off, in seconds, when it sent one. */
  get retryAfterSeconds(): number | undefined {
    return this.problem.retryAfter;
  }

  /**
   * Copy the learner should actually see. Never a raw status code: "Error 429" tells
   * someone practising Spanish nothing about what to do next. Kept identical to
   * Problem.userMessage on iOS and Problem.userMessage on Android.
   */
  get userMessage(): string {
    const detail = this.problem.detail;
    if (detail !== undefined && detail.length > 0) return detail;
    if (this.status === 401 || this.status === 403) return 'Please sign in again.';
    if (this.status === 404) return "We couldn't find that.";
    if (this.status === 408 || this.status === 504) return 'That took too long. Try again?';
    if (this.status === 429) return 'Slow down a moment, then try again.';
    if (this.status >= 500) return "Something went wrong on our side. We're on it.";
    return this.problem.title;
  }

  /** True when the learner hit a metered limit rather than an error. */
  get isQuota(): boolean {
    return this.problem.type.endsWith('/quota-exhausted');
  }

  get isEntitlement(): boolean {
    return this.problem.type.endsWith('/entitlement-required');
  }

  get isStaleVersion(): boolean {
    return this.problem.type.endsWith('/stale-version');
  }

  get fieldIssues(): readonly { path: string; message: string }[] {
    return this.problem.issues ?? [];
  }
}

export class NetworkError extends Error {
  constructor(cause: unknown) {
    super(cause instanceof Error ? cause.message : String(cause));
    this.name = 'NetworkError';
  }
}

export interface TokenPair {
  readonly accessToken: string;
  readonly refreshToken: string;
  readonly expiresAt: number;
}

export interface TokenStore {
  get(): Promise<TokenPair | null>;
  set(tokens: TokenPair | null): Promise<void>;
}

/**
 * Where a client keeps content for offline use.
 *
 * The platform owns the storage — IndexedDB on the web, a file on each phone — and this
 * owns the policy of what goes in it. Keyed by the request URL, so a cached lesson is
 * found by the same call that fetched it.
 */
export interface ResponseStore {
  read(key: string): Promise<unknown | null>;
  write(key: string, value: unknown, bytes: number): Promise<void>;
  /** Total bytes held, for the storage figure the settings screen shows. */
  size(): Promise<number>;
  /** Drops everything, which is what "Remove downloads" means. */
  clear(): Promise<void>;
}

/** In-memory store. Platforms override this with Keychain / Keystore / cookies. */
export class MemoryTokenStore implements TokenStore {
  private tokens: TokenPair | null = null;
  async get(): Promise<TokenPair | null> {
    return this.tokens;
  }
  async set(tokens: TokenPair | null): Promise<void> {
    this.tokens = tokens;
  }
}

export interface ClientOptions {
  readonly baseUrl: string;
  readonly tokens?: TokenStore;
  readonly fetchImpl?: typeof fetch;
  /** Correlates a client report with server logs. */
  readonly traceIdFactory?: () => string;
  /** Called when the refresh token is rejected and the learner must sign in again. */
  readonly onUnauthenticated?: () => void;
  /** Called whenever tokens change, so a platform can persist them. */
  readonly onTokens?: (tokens: TokenPair | null) => void;
  /**
   * Where to keep content for offline use, when this platform has somewhere.
   *
   * `Offline lessons` is sold on the paywall of all three clients and nothing
   * implemented it. With a store, every route the contract marks `cacheable` is kept
   * as it is fetched and served from here when the network is gone.
   */
  readonly offline?: ResponseStore;
  readonly timeoutMs?: number;
}

interface RequestOptions {
  readonly idempotencyKey?: string;
  readonly ifMatch?: string;
  readonly etag?: string;
  readonly signal?: AbortSignal;
  /** Internal: prevents a refresh loop. */
  readonly skipRefresh?: boolean;
}

export class VibeLangClient {
  private readonly tokens: TokenStore;
  /** Set when this platform can keep content offline; absent means online-only. */
  readonly offline: ResponseStore | undefined;
  private readonly fetchImpl: typeof fetch;
  private readonly traceIdFactory: () => string;
  private readonly timeoutMs: number;
  /** Shared in-flight refresh, so N concurrent 401s cause one refresh, not N. */
  private refreshing: Promise<TokenPair | null> | null = null;

  constructor(private readonly options: ClientOptions) {
    this.tokens = options.tokens ?? new MemoryTokenStore();
    this.offline = options.offline;
    /**
     * Bound to the global, not stored bare.
     *
     * `this.fetchImpl = fetch` followed by `this.fetchImpl(...)` calls the native
     * function with the client as its receiver, and a browser rejects that:
     * `TypeError: Failed to execute 'fetch' on 'Window': Illegal invocation`. So **every
     * request from the web app failed**, from the silent anonymous sign-in onwards —
     * the app mounted, both bootstrap calls threw into their own catch blocks, and a
     * learner got onboarding forever.
     *
     * Nothing caught it because Node's `fetch` does not check its receiver. Every unit
     * test, every fixture test and the whole live-response suite run on undici, where
     * the bare reference works perfectly.
     */
    this.fetchImpl = options.fetchImpl ?? ((input, init) => globalThis.fetch(input, init));
    this.traceIdFactory = options.traceIdFactory ?? (() => crypto.randomUUID());
    this.timeoutMs = options.timeoutMs ?? 20_000;
  }

  async setTokens(tokens: TokenPair | null): Promise<void> {
    await this.tokens.set(tokens);
    this.options.onTokens?.(tokens);
  }

  async getTokens(): Promise<TokenPair | null> {
    return this.tokens.get();
  }

  /**
   * Signs out on the server, then locally.
   *
   * The Swift and Kotlin clients each had a `signOut`; this one did not, so the web had
   * no way to sign out at all. All three now revoke the refresh-token family first —
   * dropping the tokens from one device while the family stays valid is the difference
   * between signing out of a shared computer and closing the tab.
   *
   * The local clear happens even if the call fails: the person asked to be signed out,
   * and refusing because the network is down is the wrong kind of strict.
   */
  async signOut(): Promise<void> {
    try {
      await this.call('authLogout', { body: {} });
    } catch {
      // Already expired, revoked, or offline — all of which end the same way.
    }
    await this.tokens.set(null);
  }

  /** The headers one request carries: trace, auth, content type, idempotency, ETags. */
  private async headersFor(
    name: RouteName,
    hasBody: boolean,
    options: RequestOptions,
  ): Promise<Headers> {
    const route = routes[name];
    const headers = new Headers({ accept: 'application/json' });
    headers.set('x-trace-id', this.traceIdFactory());

    if (route.auth !== 'none') {
      const tokens = await this.tokens.get();
      if (tokens !== null) headers.set('authorization', `Bearer ${tokens.accessToken}`);
    }

    if (hasBody) headers.set('content-type', 'application/json');

    // Idempotency by default on state-creating POSTs. The caller may supply a stable key
    // (e.g. a client-generated session ULID) to make a retry a true no-op.
    // `in` rather than property access: routes that are not idempotent omit the field.
    if ('idempotent' in route && route.idempotent === true) {
      headers.set('idempotency-key', options.idempotencyKey ?? crypto.randomUUID());
    }
    if (options.ifMatch !== undefined) headers.set('if-match', options.ifMatch);
    if (options.etag !== undefined) headers.set('if-none-match', options.etag);

    return headers;
  }

  /**
   * The key a response is kept under, or `null` when it must not be kept.
   *
   * Only GETs the contract itself marks `cacheable`: a cached mutation would be a lie,
   * and a cached personal screen would be another learner's data on a shared device.
   */
  private cacheKeyFor(name: RouteName, url: URL): string | null {
    if (this.offline === undefined) return null;
    const route = routes[name];
    if (route.method !== 'GET') return null;
    if (!('cacheable' in route) || route.cacheable !== true) return null;
    return url.toString();
  }

  /** The stored copy, if there is one that still matches the contract. */
  private async readOffline(name: RouteName, cacheKey: string | null): Promise<unknown | null> {
    if (cacheKey === null) return null;
    const cached = await this.offline?.read(cacheKey);
    if (cached === null || cached === undefined) return null;
    // Content stored by an older build may no longer parse; an error screen beats
    // handing a screen a shape it was not written for.
    const parsed = routes[name].response.safeParse(cached);
    return parsed.success ? parsed.data : null;
  }

  /**
   * Calls a named route. Params, query, body and response are all typed from the
   * contract, so a route rename is a compile error rather than a 404 in production.
   */
  async call<N extends RouteName>(
    name: N,
    input: {
      params?: RouteParams<N>;
      query?: RouteQuery<N>;
      body?: RouteBody<N>;
    } = {},
    options: RequestOptions = {},
  ): Promise<RouteResponse<N>> {
    const route = routes[name];
    const path = buildPath(route.path, (input.params ?? {}) as Record<string, string | number>);

    const url = new URL(`${this.options.baseUrl}${API_BASE_PATH}${path}`);
    if (input.query !== undefined) {
      for (const [key, value] of Object.entries(input.query as Record<string, unknown>)) {
        if (value === undefined || value === null) continue;
        url.searchParams.set(key, String(value));
      }
    }

    const headers = await this.headersFor(name, input.body !== undefined, options);

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(new Error('request timeout')), this.timeoutMs);
    if (options.signal !== undefined) {
      options.signal.addEventListener('abort', () => controller.abort(options.signal?.reason), {
        once: true,
      });
    }

    const cacheKey = this.cacheKeyFor(name, url);

    let response: Response;
    try {
      response = await this.fetchImpl(url.toString(), {
        method: route.method,
        headers,
        ...(input.body !== undefined ? { body: JSON.stringify(input.body) } : {}),
        signal: controller.signal,
      });
    } catch (error) {
      // The network is gone. For cacheable content this is exactly the moment the
      // download was for, so the stored copy answers instead of an error screen.
      const cached = await this.readOffline(name, cacheKey);
      if (cached !== null) return cached as RouteResponse<N>;
      throw new NetworkError(error);
    } finally {
      clearTimeout(timer);
    }

    // 304 means the caller's cached copy is still good.
    if (response.status === 304) {
      return undefined as unknown as RouteResponse<N>;
    }

    if (response.status === 401 && route.auth !== 'none' && options.skipRefresh !== true) {
      const refreshed = await this.refresh();
      if (refreshed !== null) {
        return this.call(name, input, { ...options, skipRefresh: true });
      }
      this.options.onUnauthenticated?.();
    }

    const text = await response.text();
    const payload = text.length === 0 ? null : (JSON.parse(text) as unknown);

    if (!response.ok) {
      throw new ApiError(normalizeProblem(payload, response.status), response.status);
    }

    // Validate against the contract. A server that drifts from the contract fails here,
    // where it is debuggable, rather than three screens later.
    const parsed = route.response.safeParse(payload);
    if (!parsed.success) {
      throw new ApiError(
        {
          type: 'https://vibelang.app/problems/internal',
          title: 'The server sent something unexpected.',
          status: 500,
          retryable: true,
          detail: parsed.error.issues
            .map((i) => `${i.path.join('.') || '(root)'}: ${i.message}`)
            .join('; '),
        },
        500,
      );
    }

    // Kept as it is fetched, so a lesson the learner opened once is there on the train
    // whether or not they thought to download anything.
    if (cacheKey !== null) {
      await this.offline?.write(cacheKey, parsed.data, text.length).catch(() => undefined);
    }

    return parsed.data as RouteResponse<N>;
  }

  /** Coalesced token refresh. */
  private async refresh(): Promise<TokenPair | null> {
    if (this.refreshing !== null) return this.refreshing;

    this.refreshing = (async () => {
      try {
        const current = await this.tokens.get();
        if (current === null) return null;

        const result = await this.call(
          'authRefresh',
          { body: { refreshToken: current.refreshToken } },
          { skipRefresh: true },
        );

        const next: TokenPair = {
          accessToken: result.accessToken,
          refreshToken: result.refreshToken,
          expiresAt: result.expiresAt,
        };
        await this.setTokens(next);
        return next;
      } catch {
        await this.setTokens(null);
        return null;
      } finally {
        this.refreshing = null;
      }
    })();

    return this.refreshing;
  }

  // ── Convenience wrappers for the paths the clients use most ─────────────────

  async signInAnonymously(
    input: RouteBody<'authAnonymous'>,
  ): Promise<RouteResponse<'authAnonymous'>> {
    const result = await this.call('authAnonymous', { body: input });
    await this.setTokens({
      accessToken: result.accessToken,
      refreshToken: result.refreshToken,
      expiresAt: result.expiresAt,
    });
    return result;
  }

  async today(courseId: string, etag?: string): Promise<RouteResponse<'getToday'>> {
    return this.call('getToday', { params: { courseId } }, etag === undefined ? {} : { etag });
  }

  /**
   * Starts a session using the client-generated id as the idempotency key, so a flaky
   * network produces one session rather than two.
   */
  /**
   * Resolves a media URL the API returned into one a player can actually fetch.
   *
   * Every `audioUrl`, `imageUrl` and friend comes back root-relative — `/v1/audio/<key>`
   * — which `MediaUrlSchema` explicitly permits. Relative to *what* is the part no
   * client was handling: an `<audio src>` resolves it against the page origin, and the
   * web app is served from Pages while the API is a Worker on another host, so every
   * recording 404ed. A native player given the bare string has no origin to resolve
   * against at all.
   *
   * Absolute URLs pass through untouched, so a future move to a CDN hostname needs no
   * change here.
   */
  mediaUrl(path: string | null): string | null {
    if (path === null || path.length === 0) return null;
    if (/^https?:\/\//.test(path)) return path;
    return `${this.options.baseUrl}${path}`;
  }

  async startSession(body: RouteBody<'createSession'>): Promise<RouteResponse<'createSession'>> {
    return this.call('createSession', { body }, { idempotencyKey: body.id });
  }

  async submitTurn(
    sessionId: string,
    index: number,
    body: RouteBody<'submitTurn'>,
  ): Promise<RouteResponse<'submitTurn'>> {
    return this.call(
      'submitTurn',
      { params: { sessionId, index }, body },
      { idempotencyKey: `${sessionId}:${index}` },
    );
  }

  async completeSession(
    sessionId: string,
    body: RouteBody<'completeSession'> = {},
  ): Promise<RouteResponse<'completeSession'>> {
    return this.call(
      'completeSession',
      { params: { sessionId }, body },
      { idempotencyKey: `complete:${sessionId}` },
    );
  }

  async reviewQueue(query: RouteQuery<'getReviewQueue'>): Promise<RouteResponse<'getReviewQueue'>> {
    return this.call('getReviewQueue', { query });
  }

  async submitGrades(
    body: RouteBody<'submitReviewGrades'>,
  ): Promise<RouteResponse<'submitReviewGrades'>> {
    return this.call('submitReviewGrades', { body }, { idempotencyKey: body.batchId });
  }

  async appeal(body: RouteBody<'appealScore'>): Promise<RouteResponse<'appealScore'>> {
    return this.call('appealScore', { body }, { idempotencyKey: `appeal:${body.utteranceId}` });
  }

  /** Opens the live-session socket. Returns the raw socket; protocol framing is shared. */
  liveSessionUrl(sessionId: string, accessToken: string): string {
    const base = this.options.baseUrl.replace(/^http/, 'ws');
    return `${base}${API_BASE_PATH}/sessions/${sessionId}/live?access_token=${encodeURIComponent(accessToken)}`;
  }
}

function normalizeProblem(payload: unknown, status: number): Problem {
  if (typeof payload === 'object' && payload !== null && 'type' in payload && 'title' in payload) {
    const candidate = payload as Partial<Problem>;
    return {
      type: candidate.type ?? 'https://vibelang.app/problems/internal',
      title: candidate.title ?? 'Something went wrong.',
      status: candidate.status ?? status,
      retryable: candidate.retryable ?? status >= 500,
      ...(candidate.detail !== undefined ? { detail: candidate.detail } : {}),
      ...(candidate.issues !== undefined ? { issues: candidate.issues } : {}),
      ...(candidate.traceId !== undefined ? { traceId: candidate.traceId } : {}),
      ...(candidate.retryAfter !== undefined ? { retryAfter: candidate.retryAfter } : {}),
    };
  }

  return {
    type: 'https://vibelang.app/problems/internal',
    title: 'Something went wrong.',
    status,
    retryable: status >= 500 || status === 429,
  };
}

/** Client-side ULID, used for idempotency keys and client-generated session ids. */
export function clientUlid(now: number = Date.now()): string {
  const ENCODING = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
  let time = '';
  let remaining = now;
  for (let i = 0; i < 10; i++) {
    time = (ENCODING[remaining % 32] ?? '0') + time;
    remaining = Math.floor(remaining / 32);
  }
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  const random = Array.from(bytes, (b) => ENCODING[b % 32] ?? '0').join('');
  return time + random;
}

export * from './tracker.js';
export type { RouteBody, RouteName, RouteParams, RouteQuery, RouteResponse };
